import React, { useState, useEffect, useContext } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Copybar from '../../images/copy.png';

import { format, differenceInDays } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import toMoney from '../../functions/toMoney';
import Summary from '../../components/Summary';
import PaymentModal from '../../components/PaymentModal';
import PaymentDetailsPanel from '../../components/PaymentDetailsPanel';
import { getCompanyInfo } from '../../services/company';
import isPixEnabled, {
  isTotalBankBoletoEnabled,
} from '../../functions/pixCompanies';
import isRecurrent from '../../functions/recurrence';
import getBoletoEligibility, {
  MOTIVO_SEM_DADOS_BANCARIOS,
  MOTIVO_BANCO_NAO_HOMOLOGADO,
  MOTIVO_CODIGO_BARRAS_INVALIDO,
  MOTIVO_ATRASO_EXCEDIDO,
  LIMITE_ATRASO_DIAS,
} from '../../functions/boletoEligibility';
import BANCOS_HOMOLOGADOS, {
  getBancoCor,
  getBancoHomologado,
  isBancoHomologado,
} from '../../functions/bancosHomologados';
import { getBancoLogo } from '../../functions/bancoLogos';
import stripLinhaDigitavel from '../../functions/linhaDigitavel';
import BoletoPrint from '../../components/BoletoPrint';
import BoletoBarcode from '../../components/BoletoBarcode';
import { apiCharge } from '../../services/api';
import apiVirtus from '../../services/apiVirtus';
import AuthContext from '../../contexts/auth';
import LayoutContext from '../../contexts/layout';
import {
  getDiscountRules,
  getAnticipationRules,
} from '../../services/discountRules';
import { getAnticipationLimit } from '../../functions/paymentRules';

function Table({ tab, loading, contract, onChange, onSelected }) {
  const { user } = useContext(AuthContext);
  const { contact } = useContext(LayoutContext);
  const token = JSON.parse(sessionStorage.getItem('@portal-cliente/token'));
  const group = JSON.parse(sessionStorage.getItem('@portal-cliente/group'));
  const [selected, setSelected] = useState([]);
  const [allCharges, setAllCharges] = useState([]);
  const [filter, setFilter] = useState({
    overdue: false,
    open: false,
  });
  const [totalChargesOpen, setTotalChargesOpen] = useState(0);
  const [totalChargesOverdue, setTotalChargesOverdue] = useState(0);
  const [chargesPagination, setChargesPagination] = useState([]);
  const [suspended, setSuspend] = useState(false);
  const [page, setPage] = useState(1);

  // Modal de "Gerar segunda via" (DTGD-1311): sempre busca o título de novo
  // antes de exibir, nunca reaproveita o dado já carregado na tela. Nenhuma
  // chamada a TotalBank/registro bancário — o dado exibido é sempre o que
  // já veio do ERP via api-charge. Esse é o caminho preferido sempre que o
  // título já tem `boleto` (ver handleBoletoAction).
  const [showSegundaVia, setShowSegundaVia] = useState(false);
  const [segundaViaLoading, setSegundaViaLoading] = useState(false);
  const [segundaViaTitulo, setSegundaViaTitulo] = useState(null);
  const [segundaViaError, setSegundaViaError] = useState('');
  const [copiedSegundaVia, setCopiedSegundaVia] = useState(false);

  // Modal de boleto via backend-virtus/TotalBank (fluxo anterior à
  // DTGD-1311) — mantido como fallback só para títulos que a carga do ERP
  // ainda não populou com `boleto` (clientes/títulos não migrados). Ver
  // BE-02 no review: sem isso, esses títulos ficam sem nenhuma forma de
  // emitir boleto pelo portal.
  const [showBoleto, setShowBoleto] = useState(false);
  const [boletoLoading, setBoletoLoading] = useState(false);
  const [boletoData, setBoletoData] = useState(null);
  const [boletoError, setBoletoError] = useState('');
  const [copiedBoleto, setCopiedBoleto] = useState('');
  // Habilita o fallback só para o grupo de empresas que de fato usa
  // TotalBank hoje (mesma lista do PIX — ver pixCompanies.js).
  const boletoEnabled = isTotalBankBoletoEnabled(contract?.cnpj);

  // Painel "Detalhes do pagamento" (CA05) — linha clicada na tabela,
  // exibida no painel lateral (col-md-4), no lugar do Summary.
  const [detailChave, setDetailChave] = useState(null);

  // Modal de pagamento (PIX + cartão) — só para empresas com PIX habilitado.
  const [showPayment, setShowPayment] = useState(false);
  const pixEnabled = isPixEnabled(contract?.cnpj);
  // "Pagar agora" a partir do painel de detalhes: a seleção (setSelected +
  // onSelected) precisa refletir no componente pai (Dashboard, via
  // props.onChange/handleCheckout) antes de navegar — como os dois setters
  // são assíncronos, adia a ação de pagar para depois do próximo render
  // (useEffect abaixo), evitando ler `selected` desatualizado no pai.
  const [pendingPagarAgora, setPendingPagarAgora] = useState(false);

  // Formas de pagamento consideradas recorrência (config por empresa). Títulos
  // com essas formas não são pagáveis pelo portal.
  const [recurrentMethods, setRecurrentMethods] = useState('');

  // Regras de desconto por antecipação (por CNPJ). Vazio = empresa sem regras
  // (fluxo segue normal, sem desconto). `parcelsLimit` = limite de títulos
  // selecionáveis (regra de antecipação); null = sem limite.
  const [discountRules, setDiscountRules] = useState([]);
  const [parcelsLimit, setParcelsLimit] = useState(null);

  // Seleção cumulativa por vencimento: a seleção é sempre um prefixo da lista de
  // títulos pagáveis (ordenada por vencimento). Não é possível selecionar um
  // título sem ter os anteriores (mais antigos) selecionados; selecionar um mais
  // recente seleciona automaticamente todos os anteriores. Replica o comportamento
  // do app de cobrança (usePayment.toggleTitle).
  const selectableCharges = allCharges.filter(c => !c.isRecurrent);
  const posByChave = new Map(selectableCharges.map((c, i) => [c.chave, i]));
  const selectedKeys = new Set(selected.map(s => s.chave));

  // Mensagem exibida quando o título tem boleto do ERP mas não é elegível
  // para segunda via pelo portal (CA13/CA10).
  const getMotivoIndisponibilidade = reason => {
    const telefone = contact?.landline || contact?.mobile || '';
    const contatoTexto = telefone
      ? ` Ligue ${telefone} para a central de atendimento.`
      : ' Entre em contato com a central de atendimento.';
    if (reason === MOTIVO_BANCO_NAO_HOMOLOGADO) {
      const nomes = BANCOS_HOMOLOGADOS.map(banco => banco.nome).join(', ');
      return `Emissão pelo portal disponível apenas para: ${nomes}.${contatoTexto}`;
    }
    if (reason === MOTIVO_ATRASO_EXCEDIDO) {
      return `Este título está vencido há mais de ${LIMITE_ATRASO_DIAS} dias.${contatoTexto}`;
    }
    if (reason === MOTIVO_CODIGO_BARRAS_INVALIDO) {
      return `Não foi possível validar o código de barras deste boleto.${contatoTexto}`;
    }
    return `O contrato ainda não possui dados bancários cadastrados.${contatoTexto}`;
  };

  // Segunda via (DTGD-1311/CA04) depende só dos dados do próprio título, não
  // da posição dele na lista — a ordem de vencimento vale só para o
  // pagamento pelo portal (seleção cumulativa, ver handleCheck). Retorna o
  // motivo de a ação ficar desabilitada, ou null quando ela fica habilitada:
  // - Atraso maior que o limite (CA10): desabilitada, orientando o contato
  //   com a central — inclusive para título sem `boleto` com fallback do
  //   backend-virtus, que não pode emitir boleto novo para esse título.
  // - Sem dados bancários e sem o fallback: não há o que imprimir; orienta o
  //   "Pagar agora" (cartão/PIX), que não depende de boleto — mesmo
  //   comportamento de antes da DTGD-1311, em que o botão de boleto nem
  //   aparecia nesse caso.
  // Banco não homologado (CA13) e DV inválido seguem habilitados, abrindo o
  // modal com o aviso.
  const getMotivoBoletoBloqueado = (row, eligibility) => {
    if (eligibility.reason === MOTIVO_ATRASO_EXCEDIDO) {
      return getMotivoIndisponibilidade(MOTIVO_ATRASO_EXCEDIDO);
    }
    if (
      eligibility.reason === MOTIVO_SEM_DADOS_BANCARIOS &&
      !(!row.boleto && boletoEnabled)
    ) {
      return 'Boleto indisponível para esta parcela: o contrato ainda não possui dados bancários cadastrados. Use "Pagar agora" para pagar por outra forma.';
    }
    return null;
  };

  const handleCloseSegundaVia = () => {
    setShowSegundaVia(false);
    setSegundaViaTitulo(null);
    setSegundaViaError('');
    setCopiedSegundaVia(false);
  };

  // "Gerar segunda via" (DTGD-1311): busca o título de novo (não reaproveita
  // o dado já carregado na tela) e, se ainda elegível, abre a representação
  // do boleto para impressão. Nenhuma chamada a TotalBank/registro bancário.
  const handleGerarSegundaVia = async row => {
    setSegundaViaError('');
    setSegundaViaTitulo(null);
    setSegundaViaLoading(true);
    setShowSegundaVia(true);
    try {
      const { data } = await apiCharge.get(`/v2/charges/${group}/${user.cpf}/list`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const contratoAtualizado = (data.contratos || []).find(
        c => c.codigo === contract.codigo,
      );
      const tituloAtualizado = (contratoAtualizado?.titulos_abertos || []).find(
        t => t.chave === row.chave,
      );
      if (!tituloAtualizado) {
        setSegundaViaError(
          'Este título não está mais disponível para segunda via pelo portal.',
        );
        return;
      }
      const diasAtrasoAtual = differenceInDays(
        new Date(),
        new Date(tituloAtualizado.vencto + 'T00:00:00'),
      );
      const { eligible, reason } = getBoletoEligibility(
        tituloAtualizado.boleto,
        diasAtrasoAtual,
      );
      if (!eligible) {
        setSegundaViaError(getMotivoIndisponibilidade(reason));
        return;
      }
      // `beneficiario_boleto`/`pagador_boleto` vêm uma vez por contrato (não
      // mais dentro do título) — anexa aqui pro BoletoPrint continuar
      // recebendo tudo através de `titulo`.
      setSegundaViaTitulo({
        ...tituloAtualizado,
        beneficiario_boleto: contratoAtualizado?.beneficiario_boleto || null,
        pagador_boleto: contratoAtualizado?.pagador_boleto || null,
      });
    } catch {
      setSegundaViaError(
        'Não foi possível carregar os dados do boleto. Tente novamente em instantes.',
      );
    } finally {
      setSegundaViaLoading(false);
    }
  };

  const handleCloseBoleto = () => {
    setShowBoleto(false);
    setBoletoData(null);
    setBoletoError('');
    setCopiedBoleto('');
  };

  // Fallback via backend-virtus/TotalBank (fluxo anterior à DTGD-1311) para
  // título sem `boleto` na carga do ERP — ver handleBoletoAction.
  const handleGenerateBoleto = async row => {
    setBoletoError('');
    setBoletoData(null);
    setCopiedBoleto('');
    setBoletoLoading(true);
    setShowBoleto(true);
    try {
      const { data } = await apiVirtus.post(
        `/cobranca/portal/boleto/${contract.cnpj}/${user.cpf}/generate`,
        {
          contrato: row.contrato,
          chave: row.chave,
        },
        { headers: { Authorization: 'Bearer ' + token } },
      );
      setBoletoData(data);
    } catch (err) {
      setBoletoError(
        err?.response?.data?.message ||
          'Não foi possível gerar o boleto. Tente novamente em instantes.',
      );
    } finally {
      setBoletoLoading(false);
    }
  };

  // Decide entre a segunda via (dado que já veio do ERP via api-charge, sem
  // nenhuma chamada de registro bancário) e o fallback de geração via
  // backend-virtus/TotalBank — usado só quando a carga do ERP ainda não
  // populou o título com `boleto` (cliente/título não migrado). Título com
  // `boleto` NUNCA passa pelo backend-virtus.
  const handleBoletoAction = row => {
    if (row.boleto || !boletoEnabled) {
      handleGerarSegundaVia(row);
      return;
    }
    handleGenerateBoleto(row);
  };

  const mountPagination = (data, max) => {
    const chargesPagination = [[]];
    let index = 0;

    for (let i = 0; i < data.length; i++) {
      if (chargesPagination[index] === undefined) {
        chargesPagination[index] = [];
      }

      chargesPagination[index].push(data[i]);

      if ((i + 1) % max === 0) {
        index = index + 1;
      }
    }

    return chargesPagination;
  };

  const handleDataFromBillsOpen = data => {
    return data.map(value => {
      const date = format(new Date(value.vencto + 'T00:00:00'), 'dd/MM/yyyy');
      // `emissao` é novo no payload do ERP (data de emissão do título) — pode
      // vir ausente/vazio em títulos antigos, ou num formato inesperado num
      // registro legado; nesses casos não formata em vez de quebrar a tela.
      let emissao = '';
      if (value.emissao) {
        try {
          emissao = format(new Date(value.emissao + 'T00:00:00'), 'dd/MM/yyyy');
        } catch {
          emissao = '';
        }
      }
      const diasAtraso = differenceInDays(
        new Date(),
        new Date(value.vencto + 'T00:00:00'),
      );
      const status = diasAtraso > 1 ? 'Em atraso' : 'Em aberto';
      const price = toMoney(value.valor_titulo);
      return {
        check: false,
        contrato: contract.codigo,
        chave: value.chave,
        date,
        emissao,
        status,
        price,
        codeBar: value?.boleto?.linha_digitavel || '',
        barCode: value?.boleto?.codigo_de_barras || '',
        boleto: value.boleto,
        diasAtraso,
        valorRaw: value.valor_titulo,
        vencimentoRaw: value.vencto,
        isRecurrent: isRecurrent(value.forma_pagamento, recurrentMethods),
      };
    });
  };

  const sortCharges = data => {
    return data.sort((a,b) => {
      return new Date(a.date.split('/')[2], parseInt(a.date.split('/')[1]) -1, a.date.split('/')[0]) - new Date(b.date.split('/')[2], parseInt(b.date.split('/')[1]) -1, b.date.split('/')[0])
    });
  }

  const countCharges = totalCharges => {
    if (!totalCharges) {
      setTotalChargesOpen(0);
      return setTotalChargesOverdue(0);
    }
    setTotalChargesOpen(totalCharges.filter(c => c.status === 'Em aberto').length);
    setTotalChargesOverdue(totalCharges.filter(c => c.status === 'Em atraso').length);
  };

  const mountLines = () => {
    if (contract && contract.titulos_abertos.length > 0) {
      const billsOpen = handleDataFromBillsOpen(contract.titulos_abertos);
      if (!!billsOpen.length) {
        countCharges(billsOpen);
        const chargesSort = sortCharges(billsOpen);
        return setAllCharges(chargesSort);
      }
      countCharges(false);
      return setAllCharges([]);
    }
    countCharges(false);
    return setAllCharges([]);
  }

  // Remove da lista os títulos liquidados via PIX sem recarregar a tela.
  const handlePaid = paidKeys => {
    if (!paidKeys || !paidKeys.length) return;
    const next = allCharges.filter(c => !paidKeys.includes(c.chave));
    setAllCharges(next);
    countCharges(next.length ? next : false);
    setSelected([]);
    onSelected([]);
    setFilter({ open: false, overdue: false });
    setPage(1);
  };

  const handleFilter = type => {
    if (type === 'open') setFilter({ open: !filter.open, overdue: false });
    if (type === 'overdue')
      setFilter({ overdue: !filter.overdue, open: false });
  };

  const handlePagination = direction => {
    if (direction === 'left' && page > 0) setPage(page - 1);
    if (direction === 'right' && page < chargesPagination.length)
      setPage(page + 1);
    if (typeof direction === 'number') setPage(direction);
  };

  const renderTableHeader = () => (
    <div>
      <div className="titulo-elementos d-flex bd-highlight">
        <div className="d-flex flex-row">
          <div className="flex-grow-1 bd-highlight">
            <h6 className="titulo-card">Últimos lançamentos</h6>
          </div>
        </div>
        <div className="filtrar-por-mobile d-flex ms-auto">
          <div className="filtrar-por bd-highlight">Filtrar por:</div>
          <div
            className={
              filter.open
                ? 'btn-filtro-card-active btn-filtro-card bd-highlight'
                : 'btn-filtro-card bd-highlight'
            }
            onClick={() => handleFilter('open')}
          >
            Em aberto
            <span className="cor-em-aberto-numero">
              {` ${totalChargesOpen}`}
            </span>
          </div>
          <div
            className={
              filter.overdue
                ? 'btn-filtro-card-active btn-filtro-card bd-highlight'
                : 'btn-filtro-card bd-highlight'
            }
            onClick={() => handleFilter('overdue')}
          >
            Em atraso
            <span className="cor-em-atraso-numero">
              {` ${totalChargesOverdue}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Seleção sempre em prefixo por vencimento. Clicar num título define quantos
  // títulos (a partir do mais antigo) ficam selecionados:
  //  - título ainda não selecionado -> seleciona até ele (inclui os anteriores);
  //  - título já selecionado -> deseleciona ele e todos os posteriores.
  const handleCheck = c => {
    const pos = posByChave.get(c.chave);
    if (pos === undefined) return;
    const currentCount = selected.length;
    const newCount = pos < currentCount ? pos : pos + 1;
    // Respeita o limite de antecipação (permite desmarcar).
    if (parcelsLimit != null && newCount > parcelsLimit) return;
    const newSelected = selectableCharges.slice(0, newCount);
    setSelected(newSelected);
    onSelected(newSelected);
  };

  // "Pagar agora" do painel de detalhes: seleciona o título (respeitando o
  // prefixo por vencimento e o limite de antecipação) e aciona o mesmo
  // fluxo de pagamento do botão "Pagar agora" da barra de selecionados.
  const handlePagarAgoraDetail = row => {
    const pos = posByChave.get(row.chave);
    if (pos === undefined) return;
    const newCount = Math.max(selected.length, pos + 1);
    if (parcelsLimit != null && newCount > parcelsLimit) return;
    const newSelected = selectableCharges.slice(0, newCount);
    setSelected(newSelected);
    onSelected(newSelected);
    setPendingPagarAgora(true);
  };

  const renderBankBadge = c => {
    const banco = c.boleto?.codigo_banco;
    if (!banco) {
      return (
        <div className="banco-emissor banco-emissor--vazio">
          <span className="banco-emissor-mark">—</span>
          <span className="banco-emissor-info">
            Sem dados bancários
            {/* Título ainda sem `boleto` na carga do ERP: se a empresa tem
                config no backend-virtus, o ícone da linha ainda tenta o
                fallback de geração (BE-02) — só fica indisponível de fato
                sem nenhuma das duas fontes. */}
            <small>
              {boletoEnabled ? 'Boleto disponível' : 'Boleto indisponível'}
            </small>
          </span>
        </div>
      );
    }
    const homologado = isBancoHomologado(banco);
    // Nome oficial da lista de homologados (mais completo/consistente) —
    // cai para o nome que veio no boleto e, por último, só o código.
    const nomeBanco =
      getBancoHomologado(banco)?.nome || c.boleto?.nome_banco || banco;
    const logo = getBancoLogo(banco);
    return (
      <div className="banco-emissor">
        {logo ? (
          <img
            className="banco-emissor-logo"
            src={logo.tile}
            alt={nomeBanco}
          />
        ) : (
          <span
            className="banco-emissor-mark"
            style={{ background: getBancoCor(banco) }}
          >
            {banco}
          </span>
        )}
        <span className="banco-emissor-info">
          {`${banco} - ${nomeBanco}`}
          {!homologado && <small>Não homologado</small>}
          {homologado && (c.boleto?.agencia || c.boleto?.conta) && (
            <small>
              Ag. {c.boleto?.agencia} · {c.boleto?.conta}
            </small>
          )}
        </span>
      </div>
    );
  };

  const renderTable = () => {
    if (loading) {
      return <Skeleton count={6} height={62} />;
    }
    if (chargesPagination.length === 0 || allCharges.length === 0) {
      return (
        <div className="alert alert-warning" role="alert">
          Você não possui faturas abertas para o contrato selecionado!
        </div>
      );
    }

    return chargesPagination[page - 1].map((c, i) => {
      // Elegibilidade para "Gerar segunda via" (CA03/CA13) — roda sempre;
      // sem `boleto` nenhum, cai no motivo "sem dados bancários" (mesmo
      // caminho de um título com boleto incompleto).
      const segundaViaEligibility = getBoletoEligibility(
        c.boleto,
        c.diasAtraso,
      );
      // Título sem `boleto` na carga do ERP, mas com fallback disponível no
      // backend-virtus (BE-02) — o ícone tenta gerar por lá em vez de ficar
      // só com o motivo "sem dados bancários".
      const temFallbackBackendVirtus = !c.boleto && boletoEnabled;
      // Mais de uma parcela selecionada para pagamento desabilita a emissão
      // de segunda via (não existe boleto combinado de parcelas).
      const boletoDesabilitadoPorSelecao = selected.length > 1;
      // Atraso excedido (CA10) ou sem dados bancários e sem fallback (CA04).
      const motivoBoletoBloqueado = getMotivoBoletoBloqueado(
        c,
        segundaViaEligibility,
      );
      const boletoIndisponivel = !!motivoBoletoBloqueado;
      const boletoBloqueado =
        boletoIndisponivel || boletoDesabilitadoPorSelecao;
      const isDetailSelected = detailChave === c.chave;

      const rowContent = (
        <div
          className={`descricao-tabela d-flex bd-highlight linha-titulo${
            isDetailSelected ? ' linha-titulo--detalhe' : ''
          }`}
          style={{
            background: selectedKeys.has(c.chave)
              ? 'var(--brand-surface, #F6F1FF)'
              : isDetailSelected
              ? 'var(--brand-surface-alt, #F9F8FF)'
              : '',
            cursor: c.isRecurrent ? 'default' : 'pointer',
          }}
        >
          <div style={{ maxWidth: '6%' }} className="flex-fill bd-highlight">
            {!c.isRecurrent && (
              <input
                className="form-check-input"
                type="checkbox"
                id={`check-${i}`}
                aria-label={`Selecionar título ${c.chave}`}
                checked={selectedKeys.has(c.chave)}
                disabled={
                  parcelsLimit != null &&
                  posByChave.get(c.chave) >= parcelsLimit &&
                  !selectedKeys.has(c.chave)
                }
                onChange={() => handleCheck(c)}
              />
            )}
          </div>

          <div
            style={{ maxWidth: '18%' }}
            className="flex-fill bd-highlight font-size-12"
          >
            <div>{c.boleto?.numero_documento || '-'}</div>
            <div className="linha-titulo-sub">Parcela 01 · {c.contrato}</div>
          </div>
          <div style={{ maxWidth: '13%' }} className="flex-fill bd-highlight">
            {c.date}
            {c.emissao && (
              <div className="linha-titulo-sub">Emissão {c.emissao}</div>
            )}
          </div>
          <div style={{ maxWidth: '13%' }} className="flex-fill bd-highlight">
            R$ {c.price}
          </div>
          <div
            style={{ maxWidth: '24%' }}
            className="flex-fill bd-highlight font-size-12"
          >
            {!c.isRecurrent && renderBankBadge(c)}
          </div>
          <div
            style={{ maxWidth: '13%' }}
            className="flex-fill bd-highlight font-size-12"
          >
            {c.isRecurrent ? (
              <span
                className="badge"
                style={{ backgroundColor: '#6495ED', color: '#fff' }}
              >
                Recorrente
              </span>
            ) : (
              <span
                className={
                  c.status === 'Em aberto'
                    ? 'status-dot status-dot--open'
                    : 'status-dot status-dot--late'
                }
              >
                {c.status}
              </span>
            )}
          </div>
          <div
            style={{ maxWidth: '13%' }}
            className="flex-fill bd-highlight text-end"
          >
            {!c.isRecurrent && (
              <button
                type="button"
                className="icon-btn-boleto"
                disabled={boletoBloqueado}
                aria-label="Segunda via do boleto"
                title={
                  boletoIndisponivel
                    ? motivoBoletoBloqueado
                    : boletoDesabilitadoPorSelecao
                    ? 'Selecione apenas uma parcela para gerar a segunda via.'
                    : segundaViaEligibility.eligible || temFallbackBackendVirtus
                    ? ''
                    : getMotivoIndisponibilidade(segundaViaEligibility.reason)
                }
                style={
                  !boletoBloqueado &&
                  !segundaViaEligibility.eligible &&
                  !temFallbackBackendVirtus
                    ? { opacity: 0.5 }
                    : undefined
                }
                onClick={e => {
                  // A linha inteira é um <label> do checkbox (abaixo) — sem
                  // isso, o clique no ícone também "clicaria" no checkbox.
                  e.preventDefault();
                  // Sempre aciona o fluxo quando habilitado, elegível ou
                  // não: quando inelegível, a busca fresca
                  // (handleGerarSegundaVia) mostra a mensagem de motivo no
                  // modal — banco não homologado (CA13) e DV inválido não
                  // ficam restritos a um tooltip. Sem `boleto` nenhum,
                  // handleBoletoAction tenta o fallback via backend-virtus
                  // (BE-02); atraso excedido (CA10) e sem dados sem fallback
                  // já vêm desabilitados (getMotivoBoletoBloqueado).
                  setDetailChave(c.chave);
                  handleBoletoAction(c);
                }}
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M6 9V3h12v6M6 18H4v-6h16v6h-2M8 14h8v7H8z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      );

      if (c.isRecurrent) {
        return <div key={c.chave}>{rowContent}</div>;
      }

      // A linha inteira é o <label> do checkbox: clicar em qualquer ponto
      // não-interativo (texto, badge, etc.) já marca o checkbox nativamente,
      // sem precisar de role="button"/tabIndex customizado na linha — o
      // checkbox e o botão de ícone continuam focáveis/operáveis por
      // teclado normalmente, como qualquer controle nativo. O "ver
      // detalhes" fica num onClick simples aqui no <label> (não é um role
      // interativo próprio, então não conflita com os controles aninhados)
      // — assim funciona mesmo quando o checkbox está desabilitado (ex.:
      // limite de antecipação), casos em que o forward nativo do <label>
      // pro checkbox não dispara nada.
      return (
        <label
          htmlFor={`check-${i}`}
          key={c.chave}
          onClick={() => setDetailChave(c.chave)}
        >
          {rowContent}
        </label>
      );
    });
  };

  const renderTablePagination = () => (
    <div className="parent">
      <div className="left"></div>
      <div className="right">
        <div className="d-flex" style={{ alignItems: 'center' }}>
          <i
            className="seta-paginacao align-self-center ri-arrow-left-s-line"
            onClick={() => { page !== 1 && handlePagination('left')}}
          ></i>
          {chargesPagination.map((pages, key) => (
            <div
              className="paginacao-lista"
              style={{
                marginRight: chargesPagination.length -1 === key ? 0 : '0.2rem',
                backgroundColor: page === key+1 ? 'var(--brand-primary, #8253c6)' : '',
                color: page === key+1 ? '#fff' : '',
                fontSize: 14,
                padding: 0,
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                height: '2.4rem',
                width: '2.4rem',
                cursor: 'pointer',
              }}
              onClick={() => handlePagination(key+1)}
            >
              {key+1}
            </div>
          ))}
          <i
            className="seta-paginacao align-self-center ri-arrow-right-s-line"
            onClick={() => handlePagination('right')}
          ></i>
        </div>
      </div>
      <div className="align-self-center">
        <div className="texto-contagem-rodape-lista">
          Exibindo {chargesPagination.length ? chargesPagination[page -1].length : 0} lançamentos
        </div>
      </div>
    </div>
  );

  const renderSelected = () => (
    <div className="active-pagamento cor-global-secundaria">
      <div className="titulo-tabela pagamento-selecionado-tabela d-flex bd-highlight">
        <div
          style={{ maxWidth: '60%' }}
          className="align-self-center cor-global-primaria selecionados-lista flex-fill bd-highlight"
        >
          {selected.length} Selecionados
          {parcelsLimit != null && (
            <span className="font-size-12" style={{ marginLeft: 6 }}>
              (limite de {parcelsLimit} parcelas)
            </span>
          )}
        </div>
        <div style={{ maxWidth: '40%' }} className="ms-auto bd-highlight">
          <button
            className="btn-pagar-agora  btn btn-primary btn-lg "
            type="submit"
            onClick={() => (pixEnabled ? setShowPayment(true) : onChange())}
          >
            Pagar agora
          </button>
        </div>
      </div>
    </div>
  );

  const renderTableTitle = () => (
    <div className="titulo-tabela d-flex bd-highlight">
      <div style={{ maxWidth: '6%' }} className="flex-fill bd-highlight"></div>
      <div style={{ maxWidth: '18%' }} className="flex-fill bd-highlight">
        Boleto
      </div>
      <div style={{ maxWidth: '13%' }} className="flex-fill bd-highlight">
        Vencimento
      </div>
      <div style={{ maxWidth: '13%' }} className="flex-fill bd-highlight">
        Valor
      </div>
      <div style={{ maxWidth: '24%' }} className="flex-fill bd-highlight">
        Banco emissor
      </div>
      <div style={{ maxWidth: '13%' }} className="flex-fill bd-highlight">
        Situação
      </div>
      <div
        style={{ maxWidth: '13%' }}
        className="flex-fill bd-highlight"
      ></div>
    </div>
  );

  useEffect(() => {
    if (contract && contract.status === 'S') setSuspend(true);
    else setSuspend(false);
  }, [contract, tab]);

  useEffect(() => {
    const data = mountPagination(allCharges, 6);
    setChargesPagination(data);
  }, [allCharges, tab]);

  useEffect(() => {
    if (!filter.open && !filter.overdue) {
      const data = mountPagination(allCharges, 6);
      setChargesPagination(data);
    } else {
      const all = allCharges.filter(
        c => c.status === (filter.open ? 'Em aberto' : 'Em atraso'),
      );
      const data = mountPagination(all, 6);
      setPage(1);
      setChargesPagination(data);
    }
  }, [filter, tab]);

  useEffect(() => {
    setSelected([]);
    onSelected([]);
    setPage(1);
  }, [tab]);

  // Limpa o painel de detalhes quando a seleção some (desmarcou o
  // checkbox ou trocou de contrato) — sem isso, o painel continuava
  // mostrando a última parcela vista mesmo sem nada selecionado.
  useEffect(() => {
    if (selected.length === 0) setDetailChave(null);
  }, [selected.length]);

  useEffect(() => {
    mountLines();
  }, [contract, tab, recurrentMethods]);

  // Dispara a ação de "Pagar agora" (painel de detalhes) só depois que a
  // seleção (setSelected/onSelected) já refletiu no componente pai — ver
  // comentário do estado `pendingPagarAgora` acima.
  useEffect(() => {
    if (!pendingPagarAgora) return;
    setPendingPagarAgora(false);
    if (pixEnabled) setShowPayment(true);
    else onChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPagarAgora]);

  // Consulta as regras de desconto/antecipação da empresa (backend-virtus).
  // Sem regras ou em caso de falha, os estados ficam vazios e o fluxo segue igual.
  useEffect(() => {
    let active = true;
    const fetchRules = async () => {
      if (!contract?.cnpj) {
        setDiscountRules([]);
        setParcelsLimit(null);
        return;
      }
      const [discount, anticipation] = await Promise.all([
        getDiscountRules(contract.cnpj),
        getAnticipationRules(contract.cnpj),
      ]);
      if (active) {
        setDiscountRules(discount);
        setParcelsLimit(getAnticipationLimit(anticipation, contract.cnpj));
      }
    };
    fetchRules();
    return () => {
      active = false;
    };
  }, [contract]);

  // Consulta as formas de pagamento recorrentes da empresa (backend-virtus).
  useEffect(() => {
    let active = true;
    const fetchRecurrentMethods = async () => {
      if (!contract?.cnpj) {
        setRecurrentMethods('');
        return;
      }
      const data = await getCompanyInfo(contract.cnpj);
      if (active) setRecurrentMethods(data?.recurrent_payment_methods || '');
    };
    fetchRecurrentMethods();
    return () => {
      active = false;
    };
  }, [contract]);

  return (
    <div className="row">
      <div className="col-md-8">
        <div className="my-1 p-3 bg-body rounded shadow-sm">
          {selected.length === 0 && renderTableHeader()}
          <div className="">
            <div className="">
              {!suspended && selected.length > 0 && renderSelected()}
              {renderTableTitle()}
              {suspended ? (
                <div className="alert alert-warning" role="alert">
                  Contrato Suspenso
                  <br />
                  Caro cliente, identificamos que o senhor(a) está com um débito
                  acima de 3 mensalidades, por esse motivo, o seu plano está
                  suspenso. Solicitamos que entre em contato por telefone ou se
                  dirija até uma agência de atendimento para colocar o seu plano
                  em dia.
                  <br />
                  Caso esteja com as mensalidades em dia, por favor,
                  desconsidere essa mensagem e entre em contato conosco.
                  Obrigado!
                </div>
              ) : (
                renderTable()
              )}
              {!suspended && renderTablePagination()}
            </div>
          </div>
        </div>
      </div>
      {(() => {
        // O painel de detalhes só aparece para uma única parcela em foco:
        // nada selecionado (só navegando) ou exatamente a parcela clicada
        // selecionada sozinha. Selecionar mais de uma (clicar numa parcela
        // mais recente arrasta as anteriores, já que não é possível pagar
        // fora de ordem) troca para o resumo de pagamento (Summary), que já
        // soma várias parcelas com desconto.
        const singleSelectedChave =
          selected.length === 1 ? selected[0].chave : null;
        const detailRow =
          detailChave &&
          (selected.length === 0 || singleSelectedChave === detailChave)
            ? allCharges.find(c => c.chave === detailChave)
            : null;
        if (!detailRow) {
          return (
            <Summary
              selected={selected}
              discountRules={discountRules}
              cnpj={contract?.cnpj}
              onPagarAgora={
                selected.length > 0
                  ? () => (pixEnabled ? setShowPayment(true) : onChange())
                  : undefined
              }
            />
          );
        }
        const detailEligibility = getBoletoEligibility(
          detailRow.boleto,
          detailRow.diasAtraso,
        );
        const detailMotivoBoletoBloqueado = getMotivoBoletoBloqueado(
          detailRow,
          detailEligibility,
        );
        return (
          <PaymentDetailsPanel
            row={detailRow}
            eligibility={detailEligibility}
            motivoIndisponibilidade={getMotivoIndisponibilidade(
              detailEligibility.reason,
            )}
            boletoIndisponivel={!!detailMotivoBoletoBloqueado}
            motivoBoletoIndisponivel={detailMotivoBoletoBloqueado}
            boletoFallbackDisponivel={!detailRow.boleto && boletoEnabled}
            onGerarSegundaVia={() => handleBoletoAction(detailRow)}
            onPagarAgora={() => handlePagarAgoraDetail(detailRow)}
          />
        );
      })()}
      {pixEnabled && (
        <PaymentModal
          show={showPayment}
          onClose={() => setShowPayment(false)}
          selected={selected}
          cnpj={contract.cnpj}
          discountRules={discountRules}
          onSelectCard={onChange}
          onPaid={handlePaid}
        />
      )}

      <Modal
        show={showSegundaVia}
        onHide={handleCloseSegundaVia}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>
            Segunda via do boleto
          </Modal.Title>
          {segundaViaTitulo?.boleto?.codigo_banco && (
            <span className="segunda-via-tag">
              {(segundaViaTitulo.boleto.nome_banco || '').toUpperCase()}{' '}
              {segundaViaTitulo.boleto.codigo_banco}
            </span>
          )}
        </Modal.Header>
        <Modal.Body>
          {segundaViaLoading && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div
                className="spinner-border text-primary"
                role="status"
                aria-label="Carregando"
              />
              <p style={{ marginTop: 16, color: '#6B7280' }}>
                Carregando boleto, aguarde...
              </p>
            </div>
          )}
          {!segundaViaLoading && segundaViaError && (
            <div className="alert alert-danger" role="alert">
              {segundaViaError}
            </div>
          )}
          {!segundaViaLoading && !segundaViaError && segundaViaTitulo && (
            <>
              {/* Na tela, só o código de barras e a linha digitável; o
                  boleto completo fica no DOM apenas para o "Imprimir". */}
              <div className="segunda-via-resumo">
                {segundaViaTitulo.boleto?.codigo_de_barras && (
                  <section>
                    <span className="segunda-via-label">Código de barras</span>
                    <div className="segunda-via-box segunda-via-box--barcode">
                      <BoletoBarcode
                        codigoBarras={segundaViaTitulo.boleto.codigo_de_barras}
                      />
                    </div>
                  </section>
                )}
                {segundaViaTitulo.boleto?.linha_digitavel && (
                  <section>
                    <span className="segunda-via-label">Linha digitável</span>
                    <div className="segunda-via-box">
                      <p className="segunda-via-linha">
                        {segundaViaTitulo.boleto.linha_digitavel}
                      </p>
                      <CopyToClipboard
                        text={stripLinhaDigitavel(
                          segundaViaTitulo.boleto.linha_digitavel,
                        )}
                        onCopy={() => setCopiedSegundaVia(true)}
                      >
                        <Button variant="primary" size="sm">
                          <img
                            src={Copybar}
                            alt="copiar"
                            style={{ width: 16, height: 16, marginRight: 6 }}
                          />
                          {copiedSegundaVia
                            ? 'Copiado!'
                            : 'Copiar linha digitável'}
                        </Button>
                      </CopyToClipboard>
                    </div>
                  </section>
                )}
              </div>
              <div className="segunda-via-print-only">
                <BoletoPrint titulo={segundaViaTitulo} />
              </div>
            </>
          )}
        </Modal.Body>
        {!segundaViaLoading && !segundaViaError && segundaViaTitulo && (
          <Modal.Footer>
            <Button variant="primary" onClick={() => window.print()}>
              Imprimir boleto
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Fallback via backend-virtus/TotalBank (BE-02): só entra em jogo
          para título sem `boleto` na carga do ERP, quando a empresa tem
          config bancária cadastrada — ver handleBoletoAction. */}
      <Modal show={showBoleto} onHide={handleCloseBoleto} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px', fontWeight: 600 }}>
            Boleto
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {boletoLoading && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div
                className="spinner-border text-primary"
                role="status"
                aria-label="Carregando"
              />
              <p style={{ marginTop: 16, color: '#6B7280' }}>
                Gerando boleto, aguarde...
              </p>
            </div>
          )}

          {!boletoLoading && boletoError && (
            <div className="alert alert-danger" role="alert">
              {boletoError}
            </div>
          )}

          {!boletoLoading && !boletoError && boletoData && (
            <>
              <div
                style={{
                  background: 'var(--brand-surface, #f5f0ff)',
                  borderRadius: 10,
                  padding: '14px 16px',
                  marginBottom: 18,
                }}
              >
                <div className="d-flex justify-content-between">
                  <span style={{ color: '#6B7280' }}>Valor</span>
                  <strong>R$ {toMoney(boletoData.valor)}</strong>
                </div>
                {boletoData.vencimento && (
                  <div className="d-flex justify-content-between mt-2">
                    <span style={{ color: '#6B7280' }}>Vencimento</span>
                    <strong>
                      {format(
                        new Date(boletoData.vencimento + 'T00:00:00'),
                        'dd/MM/yyyy',
                      )}
                    </strong>
                  </div>
                )}
                {boletoData.nossoNumero && (
                  <div className="d-flex justify-content-between mt-2">
                    <span style={{ color: '#6B7280' }}>Nosso número</span>
                    <strong>{boletoData.nossoNumero}</strong>
                  </div>
                )}
              </div>

              <label style={{ color: '#6B7280', fontSize: 13 }}>
                Linha digitável
              </label>
              <div
                style={{
                  background: 'var(--brand-surface-alt, #f9f8ff)',
                  border: '1px solid var(--brand-border, #ddd6fe)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginBottom: 14,
                  wordBreak: 'break-all',
                }}
              >
                <div style={{ marginBottom: 8 }}>
                  {boletoData.linhaDigitavel}
                </div>
                <CopyToClipboard
                  text={(boletoData.linhaDigitavel || '').trim()}
                  onCopy={() => setCopiedBoleto('linha')}
                >
                  <Button variant="primary" size="sm">
                    <img
                      src={Copybar}
                      alt="copiar"
                      style={{ width: 16, height: 16, marginRight: 6 }}
                    />
                    {copiedBoleto === 'linha' ? 'Copiado!' : 'Copiar'}
                  </Button>
                </CopyToClipboard>
              </div>

              {boletoData.codigoBarras && (
                <>
                  <label style={{ color: '#6B7280', fontSize: 13 }}>
                    Código de barras
                  </label>
                  <div
                    style={{
                      background: 'var(--brand-surface-alt, #f9f8ff)',
                      border: '1px solid var(--brand-border, #ddd6fe)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      marginBottom: 14,
                      wordBreak: 'break-all',
                    }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <BoletoBarcode
                        codigoBarras={(boletoData.codigoBarras || '').trim()}
                      />
                    </div>
                    <CopyToClipboard
                      text={(boletoData.codigoBarras || '').trim()}
                      onCopy={() => setCopiedBoleto('barras')}
                    >
                      <Button variant="primary" size="sm">
                        <img
                          src={Copybar}
                          alt="copiar"
                          style={{ width: 16, height: 16, marginRight: 6 }}
                        />
                        {copiedBoleto === 'barras' ? 'Copiado!' : 'Copiar'}
                      </Button>
                    </CopyToClipboard>
                  </div>
                </>
              )}

              {boletoData.urlBoleto && (
                <Button
                  variant="outline-primary"
                  href={boletoData.urlBoleto}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    width: 'auto',
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    marginTop: 4,
                  }}
                >
                  Abrir PDF do boleto
                </Button>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Table;
